import { useRef } from "react";
import { Upload, FileText, Image as ImageIcon, X, Save } from "lucide-react";

const ACCEPTED = ".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp";
const ACCEPT_LABEL = "PDF · DOCX · TXT · MD · PNG · JPG · WEBP";
export const MAX_MB = 12;

/**
 * Source picker: drag-and-drop file zone + paste-text area + "save for later" toggle.
 * Fully controlled — parent owns state.
 */
export default function SourceUploader({
  file, onFileChange, rawText, onRawTextChange,
  keepSource, onKeepSourceChange, onError,
}) {
  const inputRef = useRef(null);
  const dragOverRef = useRef(false);

  const pickFile = (f) => {
    if (!f) return onFileChange(null);
    if (f.size > MAX_MB * 1024 * 1024) {
      onError(`File must be under ${MAX_MB} MB.`);
      return;
    }
    onFileChange(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    dragOverRef.current = false;
    if (e.dataTransfer.files?.[0]) pickFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="qs-card p-6">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center">
          <Upload className="w-4 h-4" />
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Source material</h3>
          <p className="text-xs text-slate-500">Drop a file or paste chapter text below. {ACCEPT_LABEL}, up to {MAX_MB} MB.</p>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="mt-5 rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition bg-slate-50 border-slate-200 hover:border-slate-300"
        data-testid="ai-textbook-dropzone"
      >
        <div className="w-12 h-12 mx-auto rounded-2xl bg-white grid place-items-center border border-slate-200 text-indigo-600">
          {file ? <FileText className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />}
        </div>
        {file ? (
          <div className="mt-3">
            <p className="text-sm font-semibold text-slate-800" data-testid="ai-textbook-file-name">{file.name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); pickFile(null); }}
              className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:underline"
              data-testid="ai-textbook-clear-file"
            >
              <X className="w-3 h-3" /> Remove file
            </button>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm font-semibold text-slate-800">Drop a file here, or click to browse</p>
            <p className="text-xs text-slate-500 mt-0.5">{ACCEPT_LABEL}</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0])}
          data-testid="ai-textbook-file-input"
        />
      </div>

      <div className="mt-5">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Or paste chapter text</label>
        <textarea
          value={rawText}
          onChange={(e) => onRawTextChange(e.target.value)}
          rows={8}
          placeholder="Paste lecture notes, syllabus outlines, or chapter excerpts here…"
          className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
          data-testid="ai-textbook-raw-text"
        />
        <p className="mt-1 text-[11px] text-slate-500">Tip: If you provide both, the file takes precedence.</p>
      </div>

      <label
        className="mt-4 flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white transition cursor-pointer"
        data-testid="ai-textbook-keep-source-row"
      >
        <input
          type="checkbox"
          checked={keepSource}
          onChange={(e) => onKeepSourceChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-indigo-600"
          data-testid="ai-textbook-keep-source"
        />
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5 text-indigo-600" />
            <span className="text-sm font-semibold text-slate-800">Save this chapter for later</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">Keep the source in encrypted storage so you can regenerate more questions without re-uploading.</p>
        </div>
      </label>
    </div>
  );
}
